/**
 * Canonical Affirm eligibility + display thresholds (GB #1372, Workstream A).
 *
 * Single source of truth so PDP / cart / checkout messaging can never
 * disagree with what the payment API actually offers the buyer.
 * AFFIRM_MIN/AFFIRM_MAX are the same constants imported and multiplied
 * by 100 (dollars -> cents) at `src/pages/api/checkout/create-payment.ts`
 * (`allowAffirm` check) and `src/pages/api/checkout/update-payment.ts`
 * (`payment_method_types[]` gate) — the enforcement itself lives there,
 * this file only owns the numbers.
 *
 * HIGH_TICKET_THRESHOLD and ACH_OFFER_THRESHOLD are consumed by
 * Workstreams A2 and B1 respectively; declared here so every dollar
 * threshold in the high-ticket payment experience lives in one file.
 */

export const AFFIRM_MIN = 50;
export const AFFIRM_MAX = 30_000;
export const AFFIRM_DISPLAY_MIN = 300;
export const HIGH_TICKET_THRESHOLD = 1_000;
export const ACH_OFFER_THRESHOLD = 2_000;

/** Mirrors create-payment.ts's allowAffirm check. amountUSD is dollars, not cents. */
export function isAffirmEligible(amountUSD: number): boolean {
  return amountUSD >= AFFIRM_MIN && amountUSD <= AFFIRM_MAX;
}

/**
 * Display gate: eligible AND above the display floor. Below
 * AFFIRM_DISPLAY_MIN, Affirm may technically accept the amount but we
 * don't clutter small-ticket surfaces with financing messaging.
 */
export function shouldShowAffirmMessaging(amountUSD: number): boolean {
  return isAffirmEligible(amountUSD) && amountUSD >= AFFIRM_DISPLAY_MIN;
}

/**
 * Trims, truncates to two characters, and uppercases a raw country code;
 * collapses an empty result to `undefined` ("unknown") rather than a
 * present-but-falsy `''`.
 *
 * GB #1372 QA follow-up (Codex P2): `getValidUserDocument` initializes new
 * users with `country: ''` (src/utils/firestore/users/user.service.ts), and
 * `??` does not fall back on `''` — a naive
 * `userDoc?.country ?? userDoc?.addresses?.[0]?.country_code` derivation
 * silently locks onto `''` and never even looks at the buyer's saved
 * address. Every caller that derives a buyer country for Affirm should
 * route through this function (or `isAffirmDisplayable` below) so `''`
 * can never reach a comparison as if it meant something.
 *
 * The `.slice(0, 2)` truncation mirrors the existing legacy-country-code
 * precedent at `user.service.ts`'s `validateAddress`
 * (src/utils/firestore/users/user.service.ts:286-295): legacy/imported
 * saved addresses can carry 3-letter country codes ("USA"/"CAN") instead
 * of the ISO 3166-1 alpha-2 form every comparison in this file assumes.
 * Without truncating, a legacy 'USA' address would fail a `=== 'US'`
 * check and wrongly suppress Affirm for a US buyer, and 'CAN' would leak
 * past a `=== 'CA'`-style comparison elsewhere as an unrecognized,
 * never-matching value (Codex P2 follow-up on PR #1451, comment
 * 3594528809).
 */
export function normalizeCountryCode(raw?: string | null): string | undefined {
  const trimmed = raw?.trim().slice(0, 2).toUpperCase();
  return trimmed ? trimmed : undefined;
}

/**
 * Single source of truth for "will Affirm messaging actually show for this
 * amount + buyer country". Combines the amount gate (shouldShowAffirmMessaging)
 * with the country gate AffirmMessaging.tsx enforces (unknown or 'US' only —
 * the element is hard-coded to US Affirm terms). Callers that need to know
 * in advance whether AffirmMessaging will render something — e.g. a wrapper
 * element that would otherwise leave an empty gap when it renders null —
 * should call this instead of re-deriving the country check themselves
 * (GB #1372 QA follow-up, Codex P3: a wrapper gated on amount alone still
 * left a gap for a known non-US buyer).
 */
export function isAffirmDisplayable(amountUSD: number, buyerCountry?: string | null): boolean {
  if (!shouldShowAffirmMessaging(amountUSD)) return false;
  const country = normalizeCountryCode(buyerCountry);
  return !country || country === 'US';
}

/**
 * Will this checkout actually hand the buyer off to Affirm?
 *
 * Mirrors the routing in PaymentSection's `handlePlaceOrder`, which
 * CheckoutProcessV3's `pay()` then honours: a saved card routes to
 * `confirmCardPayment`, a FULL-balance order routes to `payWithBalance`, and
 * only what falls through reaches `confirmAffirmPayment`.
 *
 * This exists because `paymentType` is STICKY — the Stripe PaymentElement's
 * onChange is its only writer, so a buyer who selects Affirm and then picks a
 * saved card (or applies balance covering the whole order) leaves it reading
 * 'affirm' while the purchase routes elsewhere. Anything that *promises the
 * buyer an Affirm experience* — notably the 12-hour completion-window notice
 * from GB #1709 — must gate on this, not on `paymentType` alone, or it will
 * promise a redirect and a deadline for a purchase that never touches Affirm.
 * (Codex P2 on PR #1710.)
 *
 * A PARTIAL balance is deliberately not disqualifying: it is applied as a
 * deduction and the remainder still routes through the chosen method.
 */
export function willRouteToAffirm(args: {
  isSavedCard: boolean;
  balanceCoversOrder: boolean;
  paymentType: 'card' | 'affirm';
}): boolean {
  return (
    !args.isSavedCard &&
    !args.balanceCoversOrder &&
    args.paymentType === 'affirm'
  );
}
